import { FC, ReactNode } from "react";
import moment from "moment";

import { Heading, Text } from "@/ui";
import { IDataPaneProps } from "@/types";
import { NOT_AVAILABLE } from "@/config";
import { OrderPaneLoading } from "./OrderPaneLoading";
import styles from "./AdditionalDataPane.module.scss";

const DetailField = ({
    label,
    children,
}: {
    label: string;
    children: ReactNode;
}) => (
    <div className={styles.field}>
        <Text as="span" className={styles.fieldLabel} variant="body-md">
            {label}
        </Text>
        <div className={styles.fieldValue}>{children}</div>
    </div>
);

const valueText = (value?: string | null) => (
    <Text as="span" variant="body-md">
        {value || NOT_AVAILABLE}
    </Text>
);

const AdditionalDataPane: FC<IDataPaneProps> = ({ order, loading }) => {
    if (loading) {
        return <OrderPaneLoading />;
    }

    const showTreez = Boolean(order.treez_order_id);
    const showOnFleet = Boolean(order.delivery_details?.on_fleet);
    const showSecondRow = showTreez || showOnFleet;
    const trackingUrl = order.delivery_details?.on_fleet?.tracking_url;

    return (
        <div className={styles.root}>
            <div className={styles.columns}>
                <section className={styles.column}>
                    <Heading className={styles.columnTitle} level={5}>
                        {`Device (${order?.client_detail?.device || ""})`}
                    </Heading>
                    <div className={styles.fields}>
                        <DetailField label="Created At:">
                            {valueText(
                                order?.invoice_date
                                    ? moment(order.invoice_date).format(
                                          "ddd, MMM Do, YYYY - h:mm:ss a",
                                      )
                                    : null,
                            )}
                        </DetailField>
                        <DetailField label="Device:">
                            {valueText(order?.client_detail?.device)}
                        </DetailField>
                        <DetailField label="User Agent:">
                            {valueText(order?.client_detail?.user_agent)}
                        </DetailField>
                        <DetailField label="IP:">
                            {valueText(order?.client_detail?.x_forwarded_for)}
                        </DetailField>
                    </div>
                </section>

                <section className={styles.column}>
                    <Heading className={styles.columnTitle} level={5}>
                        Jane Information
                    </Heading>
                    <div className={styles.fields}>
                        <DetailField label="Shop:">
                            {valueText(order?.jane_store_name)}
                        </DetailField>
                        <DetailField label="Cart ID:">
                            {valueText(order?.jane_cart_id)}
                        </DetailField>
                        <DetailField label="Order ID:">
                            {valueText(order?.jane_order_id)}
                        </DetailField>
                        <DetailField label="Store ID:">
                            {valueText(order?.jane_store_id)}
                        </DetailField>
                    </div>
                </section>
            </div>

            {showSecondRow ? (
                <div className={styles.columns}>
                    {showTreez ? (
                        <section className={styles.column}>
                            <Heading className={styles.columnTitle} level={5}>
                                Treez Information
                            </Heading>
                            <div className={styles.fields}>
                                <DetailField label="Order ID:">
                                    {valueText(order?.treez_order_id)}
                                </DetailField>
                                <DetailField label="Order Number:">
                                    {valueText(order?.treez_order_number)}
                                </DetailField>
                            </div>
                        </section>
                    ) : null}

                    {showOnFleet ? (
                        <section className={styles.column}>
                            <Heading className={styles.columnTitle} level={5}>
                                OnFleet Information
                            </Heading>
                            <div className={styles.fields}>
                                <DetailField label="Order ID:">
                                    {valueText(
                                        order.delivery_details?.on_fleet?.id,
                                    )}
                                </DetailField>
                                <DetailField label="Tracking URL:">
                                    {trackingUrl ? (
                                        <a
                                            className={styles.externalLink}
                                            href={trackingUrl}
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            {trackingUrl}
                                        </a>
                                    ) : (
                                        valueText(null)
                                    )}
                                </DetailField>
                            </div>
                        </section>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default AdditionalDataPane;
